/**
 * `/reports/email/:mine` — the weekly machine-risk email to one site's
 * technical team. Port of the demo's `buildEmail(m)` (demo line 2595) in its
 * report pane.
 *
 * The message as a mail client would show it — From, To, Cc, Subject, the
 * plain-text body, the organisation signature — then a provenance note whose
 * links open the condition register, the week's shift records, the work
 * orders and the store lines the body was written from.
 *
 * Download saves the `.eml` (the demo's `emlText`), which opens as a draft in
 * Outlook or Mail. "Create Outlook draft" is the connector action and, as in
 * the demo, says so in a toast.
 *
 * @see src/versions/v1/modules/reports/RiskEmailScene.transform.ts
 * @see docs/logic/reports/email.md
 */

import { useMemo, useRef, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  HelpAnchor,
  InlineError,
  SkeletonRows,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { HEALTH } from '../../data/core';
import type { MineCode } from '../../schema/machine';
import type { ReportRef, ReportSource, RiskEmailPack } from '../../schema/report';
import { unpackTable } from '../../shared/columnar';
import { MailSig } from '../../shared/docs/MailSig';
import { useOemDocIdentity } from '../../shared/docs/useOemDocIdentity';
import { inWk } from '../../shared/oem-engine';
import { SourceLink } from '../../shared/oem-tiles';
import { useOemPersona } from '../../shared/OemPersonaContext';
import { isMineCode } from '../../shared/report-catalog';
import { useOemData, type ShiftDailyRow, type WorkOrder } from '../../shared/useOemData';
import { useRowsDrawer } from '../service/source-slice';
import { NotFoundScene } from '../NotFoundScene';
import { ReportSidePanel, ReportToolbar } from './ReportActions';
import { availabilityCalc } from './report-calc';
import { Para, ReportLayout, ReportPaper } from './ReportDoc';
import { emlText, riskEmailReport } from './RiskEmailScene.transform';

export function RiskEmailScene() {
  const { mine } = useParams<{ mine: string }>();
  const code = mine?.toUpperCase();
  if (!isMineCode(code)) return <NotFoundScene />;
  return <RiskEmailView mine={code} />;
}

function RiskEmailView({ mine }: { mine: MineCode }) {
  const workOrdersData = useOemData('work-orders');
  const telemetry = useOemData('telemetry');
  const parts = useOemData('parts');
  const people = useOemData('people');
  const reports = useOemData('reports');
  const oem = useOemDocIdentity();
  const { persona } = useOemPersona();
  const openRows = useRowsDrawer();
  const paperRef = useRef<HTMLDivElement>(null);
  const address: ReportRef = { kind: 'email', mine };

  const workOrders = useMemo(
    () => (workOrdersData.data ? unpackTable<WorkOrder>(workOrdersData.data.wos) : null),
    [workOrdersData.data],
  );
  const shiftDaily = useMemo(
    () => (telemetry.data ? unpackTable<ShiftDailyRow>(telemetry.data.shift_daily) : null),
    [telemetry.data],
  );

  const report = useMemo(() => {
    if (!workOrders || !shiftDaily || !parts.data || !people.data || !reports.data) return null;
    const pack = reports.data.reports.email_mi as unknown as RiskEmailPack | undefined;
    if (!pack) return null;
    return riskEmailReport(
      mine,
      {
        workOrders,
        shiftDaily,
        inventory: parts.data.inventory,
        people: people.data.people,
        pack,
      },
      oem,
    );
  }, [mine, oem, parts.data, people.data, reports.data, shiftDaily, workOrders]);

  const eml = useMemo(() => (report ? emlText(report.email) : undefined), [report]);
  const error =
    workOrdersData.error ?? telemetry.error ?? parts.error ?? people.error ?? reports.error;

  const openHealth = (ids: readonly string[], title: string) =>
    openRows('health', HEALTH.filter((h) => ids.includes(h.machine_id)), {
      title,
      filter: `machine_id in ${ids.length} machines`,
    });
  const openShift = (ids: readonly string[]) => {
    const rows = (shiftDaily ?? []).filter((s) => ids.includes(s.machine_id) && inWk(s.date));
    openRows('shift', rows, {
      title: 'Shift records this week',
      filter: `mine=${mine}, week to 06 Sep`,
      total: shiftDaily?.length,
      // Demo 2597 — the provenance note's availability calc.
      ...(report ? { calc: availabilityCalc(rows, report.availability) } : {}),
    });
  };
  const openWos = () =>
    openRows(
      'wos',
      (workOrders ?? []).filter((w) => w.mine === mine && inWk(w.created.slice(0, 10))),
      { title: 'Work orders this week', filter: `mine=${mine}, week to 06 Sep`, total: workOrders?.length },
    );
  const openLow = () =>
    openRows(
      'inv',
      (parts.data?.inventory ?? []).filter(
        (i) => i.location === `${mine} site store` && i.qty_on_hand < i.min_level,
      ),
      { title: `Below minimum · ${mine} site store`, filter: 'qty_on_hand < min_level' },
    );

  const onSource = (s: ReportSource) => {
    if (!report) return null;
    if (s.dataset === 'health') return () => openHealth(report.machineIds, 'Condition register');
    if (s.dataset === 'shift') return () => openShift(report.machineIds);
    if (s.dataset === 'wos') return openWos;
    if (s.dataset === 'inv') return openLow;
    return null;
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="RiskEmailScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-5" as="div">
            <SurfacePageHeader
              kicker="Reports"
              title={report?.title ?? 'Machine risk summary'}
              description={
                report
                  ? `${report.sub} · generated 06:05 by the assistant for ${persona.name}`
                  : 'Writing the email from the live workspace data…'
              }
              actions={<HelpAnchor doc="reports/email" />}
            />

            <ReportToolbar
              report={report}
              address={address}
              docRef={paperRef}
              eml={eml}
              copyText={report?.email.body}
            />
            <AskStrip view="reports" />

            {error ? (
              <InlineError>{`The email did not build: ${error.message}`}</InlineError>
            ) : !report ? (
              <SkeletonRows count={10} />
            ) : (
              <ReportLayout
                doc={
                  <Commentable name="EmailDocument">
                    <ReportPaper paperRef={paperRef} label={report.email.subject}>
                      <dl
                        className="mailhead mb-2.5 grid gap-0.5 border-b pb-2 text-[12px]"
                        style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
                      >
                        <HeaderLine label="From">
                          {report.email.from.name} &lt;{report.email.from.email}&gt;
                        </HeaderLine>
                        <HeaderLine label="To">{report.email.to.map((p) => p.name).join(', ')}</HeaderLine>
                        <HeaderLine label="Cc">{report.email.cc.map((p) => p.name).join(', ')}</HeaderLine>
                        <HeaderLine label="Subject">
                          <b style={{ color: 'var(--ink-primary)' }}>{report.email.subject}</b>
                        </HeaderLine>
                      </dl>
                      <pre
                        className="mail m-0 whitespace-pre-wrap text-[12.5px] leading-[1.55]"
                        style={{ fontFamily: 'inherit', color: 'var(--ink-primary)' }}
                      >
                        {report.email.body}
                      </pre>
                      <MailSig org={report.senderOrg} />
                      <Para note>
                        Machines named above resolve to the condition register (
                        <SourceLink onClick={() => openHealth(report.flaggedIds, 'Machines at risk')}>
                          open rows
                        </SourceLink>
                        ), the week&apos;s shift records (
                        <SourceLink onClick={() => openShift(report.machineIds)}>open rows</SourceLink>)
                        and work orders (<SourceLink onClick={openWos}>open rows</SourceLink>). Parts
                        lines: <SourceLink onClick={openLow}>inventory</SourceLink>.
                      </Para>
                    </ReportPaper>
                  </Commentable>
                }
                side={
                  <Commentable name="EmailSources">
                    <div className="min-w-0">
                      <ReportSidePanel report={report} address={address} onSource={onSource} />
                    </div>
                  </Commentable>
                }
              />
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** One header line of the message — the demo's `.mailhead` row. */
function HeaderLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="w-16 flex-shrink-0" style={{ color: 'var(--ink-tertiary)' }}>
        {label}
      </dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}
